import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";
import { campaignScrollAreaClass } from "./campaigns-presentation.js";

const campaignFieldTriggerBaseClass =
  "group flex w-full min-w-0 items-center justify-between gap-3 rounded-xl border border-line bg-surface px-3 text-start shadow-[0_1px_2px_rgb(15_23_42/0.04)] transition-[border-color,box-shadow] hover:border-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] data-[state=open]:border-accent data-[state=open]:ring-2 data-[state=open]:ring-[var(--color-accent-soft)] disabled:cursor-not-allowed disabled:opacity-60";

export const campaignFieldTriggerClass = cn(campaignFieldTriggerBaseClass, "min-h-12 py-2");

/** Denser variant for the Campaign Message form fields. */
export const campaignCompactFieldTriggerClass = cn(
  campaignFieldTriggerBaseClass,
  "h-9 rounded-lg py-0",
);

/**
 * Popover-backed single-select with optional search, shared by the Campaign Content
 * Channel, Sender, Template and Preview Recipient fields.
 */
export function CampaignComboSelect<T>({
  items,
  getKey,
  isSelected,
  onSelect,
  filter,
  searchable = true,
  searchPlaceholder,
  renderTrigger,
  renderOption,
  ariaLabel,
  disabled,
  emptyLabel,
  noMatchesLabel,
  testId,
  menuTestId,
  searchTestId,
  optionTestId,
  menuClassName,
  triggerClassName = campaignFieldTriggerClass,
  showChevron = true,
  align = "start",
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  isSelected: (item: T) => boolean;
  onSelect: (item: T) => void;
  filter?: (items: readonly T[], query: string) => readonly T[];
  searchable?: boolean;
  searchPlaceholder?: string;
  renderTrigger: () => ReactNode;
  renderOption: (item: T, state: { highlighted: boolean; selected: boolean }) => ReactNode;
  ariaLabel: string;
  disabled?: boolean;
  emptyLabel?: string;
  noMatchesLabel?: string;
  testId?: string;
  menuTestId?: string;
  searchTestId?: string;
  optionTestId?: (item: T) => string;
  menuClassName?: string;
  triggerClassName?: string;
  showChevron?: boolean;
  align?: "start" | "center" | "end";
}): React.JSX.Element {
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const filtered = useMemo(
    () => (searchable && filter && query ? filter(items, query) : items),
    [filter, items, query, searchable],
  );

  useEffect(() => {
    if (!open) return;
    const selectedIndex = query ? -1 : filtered.findIndex((item) => isSelected(item));
    setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
    // isSelected is an inline predicate; re-seeding only on open/query/list changes is intended.
  }, [query, open, filtered]);

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${highlight}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const choose = (item: T) => {
    onSelect(item);
    close();
  };

  const optionId = (index: number) => `${listId}-option-${index}`;

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, Math.max(filtered.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setHighlight(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setHighlight(Math.max(filtered.length - 1, 0));
    } else if (event.key === "Enter" || (!searchable && event.key === " ")) {
      event.preventDefault();
      const item = filtered[highlight];
      if (item) choose(item);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        if (next) setOpen(true);
        else close();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={triggerClassName}
          data-testid={testId}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
        >
          <span className="flex min-w-0 flex-1 items-center gap-2.5">{renderTrigger()}</span>
          {showChevron ? (
            <ChevronDown
              size={16}
              className="shrink-0 text-subtle transition-transform group-data-[state=open]:rotate-180"
              aria-hidden="true"
            />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        collisionPadding={8}
        className={cn(
          "flex max-h-[min(24rem,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)] min-w-[min(18rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl p-0 shadow-lg",
          menuClassName,
        )}
        data-testid={menuTestId}
        onOpenAutoFocus={(event) => {
          if (searchable) return;
          event.preventDefault();
          listRef.current?.focus();
        }}
      >
        {searchable ? (
          <div className="shrink-0 border-b border-line/70 p-2">
            <label className="flex h-9 items-center gap-2 rounded-lg border border-line bg-surface-muted/60 px-2.5 transition-[border-color,background-color,box-shadow] focus-within:border-line-strong focus-within:bg-surface focus-within:ring-[3px] focus-within:ring-line/60">
              <Search size={14} className="shrink-0 text-subtle" aria-hidden="true" />
              <input
                ref={searchRef}
                className="min-w-0 flex-1 border-0 bg-transparent text-[0.8125rem] outline-none placeholder:text-subtle focus-visible:shadow-none"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                data-testid={searchTestId}
                role="combobox"
                aria-expanded={open}
                aria-controls={listId}
                aria-activedescendant={filtered.length > 0 ? optionId(highlight) : undefined}
                aria-autocomplete="list"
                onKeyDown={onKeyDown}
              />
            </label>
          </div>
        ) : null}
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={searchable ? undefined : 0}
          aria-activedescendant={
            !searchable && filtered.length > 0 ? optionId(highlight) : undefined
          }
          onKeyDown={searchable ? undefined : onKeyDown}
          className={cn(
            "grid min-h-0 flex-1 content-start gap-0.5 p-1.5 outline-none focus-visible:shadow-none",
            campaignScrollAreaClass,
          )}
        >
          {items.length === 0 ? (
            <p className="m-0 px-2.5 py-4 text-center text-[0.8125rem] text-muted">{emptyLabel}</p>
          ) : filtered.length === 0 ? (
            <p className="m-0 px-2.5 py-4 text-center text-[0.8125rem] text-muted">
              {noMatchesLabel}
            </p>
          ) : (
            filtered.map((item, index) => {
              const selected = isSelected(item);
              const highlighted = index === highlight;
              return (
                <div
                  key={getKey(item)}
                  id={optionId(index)}
                  role="option"
                  aria-selected={selected}
                  data-index={index}
                  data-testid={optionTestId?.(item)}
                  className={cn(
                    "flex w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-start transition-colors",
                    selected
                      ? "bg-surface-selected"
                      : highlighted
                        ? "bg-surface-muted"
                        : null,
                    selected && highlighted ? "ring-1 ring-inset ring-accent/25" : null,
                  )}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => choose(item)}
                >
                  <span className="min-w-0 flex-1">{renderOption(item, { highlighted, selected })}</span>
                  <span className="grid size-4 shrink-0 place-items-center" aria-hidden="true">
                    {selected ? <Check size={15} className="text-accent" strokeWidth={2.5} /> : null}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
